import { z } from 'zod';

import { fetchJson } from '@/lib/http';

/**
 * `GET /api/v1/me` возвращает профиль локального пользователя:
 * `id`, `email`, `displayName`, `authProvider`, `externalId`, `createdAt`,
 * `updatedAt`, `authMode`.
 */
const nullableString = z.string().nullish();

export type CurrentUser = {
  id: string | null;
  name: string | null;
  email: string | null;
  role: string | null;
  avatarUrl: string | null;
  /** `local` — личность выдана локальным режимом AUTH_MODE=local. */
  authSource: string | null;
  /** Провайдер аутентификации: `LOCAL` или `BITRIX24`. */
  authProvider: string | null;
  /** Режим аутентификации: `local` или `bitrix24`. */
  authMode: string | null;
};

export const currentUserSchema = z
  .object({
    id: z.union([z.string(), z.number()]).nullish(),
    email: nullableString,
    displayName: nullableString,
    name: nullableString,
    role: nullableString,
    avatarUrl: nullableString,
    authProvider: nullableString,
    authMode: nullableString,
    authSource: nullableString,
  })
  .passthrough()
  .transform((raw): CurrentUser => {
    const pick = (...values: readonly (string | null | undefined)[]): string | null => {
      for (const value of values) {
        if (typeof value === 'string' && value.trim() !== '') {
          return value;
        }
      }
      return null;
    };

    return {
      id: raw.id !== null && raw.id !== undefined ? String(raw.id) : null,
      name: pick(raw.displayName, raw.name),
      email: pick(raw.email),
      role: pick(raw.role),
      avatarUrl: pick(raw.avatarUrl),
      authSource: pick(raw.authMode, raw.authSource, raw.authProvider),
      authProvider: pick(raw.authProvider),
      authMode: pick(raw.authMode),
    };
  });

export function fetchCurrentUser(signal?: AbortSignal): Promise<CurrentUser> {
  return fetchJson('/me', currentUserSchema, signal !== undefined ? { signal } : undefined);
}

export async function logoutUser(): Promise<void> {
  const response = await fetch('/api/v1/auth/logout', {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Logout failed: ${response.status}`);
  }
}
